import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { homeForRole } from "./lib/roles";
import { FirstVisitLanguagePicker } from "./components/LanguageSwitcher";

// Public pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Pricing from "./pages/Pricing";
import AccessibilityInfo from "./pages/AccessibilityInfo";
import Activation from "./pages/Activation";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Legal from "./pages/Legal";
import AuthCallback from "./pages/AuthCallback";
import Notifications from "./pages/Notifications";

// Student pages
import StudentDashboard from "./pages/student/Dashboard";
import AskLumi from "./pages/student/AskLumi";
import HomeworkScan from "./pages/student/HomeworkScan";
import Assignments from "./pages/student/Assignments";
import Progress from "./pages/student/Progress";
import Rewards from "./pages/student/Rewards";
import Onboarding from "./pages/student/Onboarding";

// Adult pages
import ParentDashboard from "./pages/parent/Dashboard";
import ParentSubscription from "./pages/parent/Subscription";
import ParentSettings from "./pages/parent/Settings";
import TeacherDashboard from "./pages/teacher/Dashboard";
import TeacherAssignments from "./pages/teacher/Assignments";
import SchoolDashboard from "./pages/school/Dashboard";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminActivations from "./pages/admin/Activations";
import AccessibilitySettingsPage from "./pages/AccessibilitySettings";

function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeForRole(user.role)} replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/accessibility" element={<AccessibilityInfo />} />
            <Route path="/activation" element={<Activation />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Auth home */}
            <Route path="/app" element={<RequireAuth><RoleHome /></RequireAuth>} />
            <Route path="/app/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />

            {/* Student */}
            <Route path="/app/student" element={<RequireAuth roles={["student"]}><StudentDashboard /></RequireAuth>} />
            <Route path="/app/student/onboarding" element={<RequireAuth roles={["student"]}><Onboarding /></RequireAuth>} />
            <Route path="/app/student/ask" element={<RequireAuth roles={["student"]}><AskLumi /></RequireAuth>} />
            <Route path="/app/student/scan" element={<RequireAuth roles={["student"]}><HomeworkScan /></RequireAuth>} />
            <Route path="/app/student/assignments" element={<RequireAuth roles={["student"]}><Assignments /></RequireAuth>} />
            <Route path="/app/student/progress" element={<RequireAuth roles={["student"]}><Progress /></RequireAuth>} />
            <Route path="/app/student/rewards" element={<RequireAuth roles={["student"]}><Rewards /></RequireAuth>} />

            {/* Parent */}
            <Route path="/app/parent" element={<RequireAuth roles={["parent"]}><ParentDashboard /></RequireAuth>} />
            <Route path="/app/parent/subscription" element={<RequireAuth roles={["parent"]}><ParentSubscription /></RequireAuth>} />
            <Route path="/app/parent/settings" element={<RequireAuth roles={["parent"]}><ParentSettings /></RequireAuth>} />

            {/* Teacher */}
            <Route path="/app/teacher" element={<RequireAuth roles={["teacher"]}><TeacherDashboard /></RequireAuth>} />
            <Route path="/app/teacher/assignments" element={<RequireAuth roles={["teacher"]}><TeacherAssignments /></RequireAuth>} />

            {/* School admin */}
            <Route path="/app/school" element={<RequireAuth roles={["school_admin"]}><SchoolDashboard /></RequireAuth>} />

            {/* Platform admin */}
            <Route path="/app/admin" element={<RequireAuth roles={["platform_admin"]}><AdminDashboard /></RequireAuth>} />
            <Route path="/app/admin/activations" element={<RequireAuth roles={["platform_admin"]}><AdminActivations /></RequireAuth>} />

            {/* Accessibility wizard (any authed user) */}
            <Route path="/app/accessibility" element={<RequireAuth><AccessibilitySettingsPage /></RequireAuth>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <FirstVisitLanguagePicker />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
